import React from "react";
import type { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import { useBuilder, BlockConfig } from "./BuilderContext";
import FloatingToolbar from "./FloatingToolbar";

interface EditableBlockWrapperProps {
  block: BlockConfig;
  children: React.ReactNode;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
}

const EditableBlockWrapper: React.FC<EditableBlockWrapperProps> = ({
  block,
  children,
  provided,
  snapshot,
}) => {
  const { state, dispatch } = useBuilder();
  const isSelected = state.selectedBlockId === block.id;

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className="relative"
    >
      {/* Outer wrapper with group hover */}
      <div className="group relative">
        {/* Drag handle -- left edge, visible on hover */}
        <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
          <span
            {...provided.dragHandleProps}
            className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${
              block.locked
                ? "opacity-30 cursor-not-allowed"
                : "cursor-grab hover:bg-slate-100 active:cursor-grabbing"
            }`}
          >
            <GripVertical className="h-4 w-4 text-slate-400 hover:text-slate-600" />
          </span>
        </div>

        {/* Selection ring when selected */}
        <div
          className={`rounded-xl transition-all ${
            isSelected
              ? "ring-2 ring-emerald-500 ring-offset-2"
              : "hover:ring-1 hover:ring-slate-200 hover:ring-offset-1"
          } ${
            snapshot.isDragging
              ? "ring-2 ring-emerald-400 shadow-lg scale-[1.01]"
              : ""
          } print:ring-0 print:ring-offset-0 print:shadow-none`}
          onClick={(e) => {
            e.stopPropagation();
            dispatch({ type: "SELECT_BLOCK", blockId: block.id });
          }}
        >
          {/* Block content */}
          {children}
        </div>

        {/* Floating toolbar -- only shown when selected and not dragging */}
        {isSelected && !snapshot.isDragging && (
          <FloatingToolbar block={block} />
        )}
      </div>
    </div>
  );
};

export default EditableBlockWrapper;
